import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  InputGroup,
  InputRightElement,
  Icon,
  Select,
  VStack,
  SimpleGrid,
  Checkbox,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';

const TeamMemberModal = ({ isOpen, onClose, type, memeberId, fetchAgain }) => {
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? null;
  const dashboard_id = userData?.dashboard?.id ?? null;
  const role_id = userData?.role?.id ?? null;
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handlePasswordVisibility = () => setShowPassword(!showPassword);

  const [memberData, setMemberData] = useState([]);

  const [dashboards, setDashboards] = useState([]);
  console.log(" TeamMemberModal ~ dashboards:", dashboards)
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [dashboardsError, setDashboardsError] = useState(false)
  console.log(" TeamMemberModal ~ selectedDashboards:", selectedDashboards)


  const fetchDashboards = async () => {
    try {
      const response = await createAPIEndPoint('dashboard').fetchAll();
      console.log(" fetchDashboards ~ response:", response)
      const dashboards = response.data.dashboards || [];

      // Map locations to format name, city, and state
      const formattedLocations = dashboards.map((dashboard) => ({
        id: dashboard.id,
        name: `${dashboard.name}`, // Display name with city & state
      }));

      setDashboards(formattedLocations);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to fetch locations');
    }
  };


  const handleDashboardChange = (id) => {
    setSelectedDashboards((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    if (dashboardsError) setDashboardsError(false);                   // clear inline error
  };

  // replace your existing fetchMember with this:
  const fetchMember = async () => {
    try {
      const response = await createAPIEndPoint('clinic_team/').fetchById(memeberId);
      const m = response.data.team_member || {};
      setMemberData(m);
      // use dashboard_ids (array) instead of dashboard_id
      setSelectedDashboards(m.dashboard_ids || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching team member');
    }
  };


  // useEffect(() => {
  //   if (memeberId) {
  //     fetchMember();
  //   }
  // }, [memeberId]);

  useEffect(() => {
    if (isOpen) {
      fetchDashboards()
    }
  }, [isOpen]);

  useEffect(() => {
    if (memeberId && type === 'edit') {
      fetchMember();
    }
  }, [memeberId, type, isOpen]);

  useEffect(() => {
    const getRoles = async () => {
      try {
        const response = await createAPIEndPoint(`clinic_roles/get_all/${clinic_id}`).fetchAll();
        setRoles(response.data.roles);
      } catch (error) {
        toast.error(error?.response?.data?.error || 'Error fetching roles');
      }
    };
    if (isOpen) {
      getRoles();
    }
  }, [isOpen]);

  const handleCreate = async (values, { resetForm }) => {
    if (selectedDashboards.length === 0) {
      setDashboardsError(true);
      toast.error('Select at least one dashboard');
      return;          // stop right here
    }
    try {
      setLoading(true);

      if (memeberId) {
        await createAPIEndPoint('clinic_team/').update(memeberId, {
          ...values,
          dashboard_id: selectedDashboards,
          clinic_id,
          role_id,
          clinic_role_id: parseInt(values.clinic_role_id, 10),
          invited_by_id: 2,

        });
      } else {
        await createAPIEndPoint('clinic_team').create({
          ...values,
          dashboard_id: selectedDashboards,
          clinic_id,
          status: 'enable',
          role_id,
          clinic_role_id: parseInt(values.clinic_role_id, 10),
          invited_by_id: 2,
        });
      }

      toast.success('New team member added successfully!');
      setLoading(false);
      fetchAgain();
      resetForm();
      onClose();
    } catch (error) {
      setLoading(false);
      console.error('Error creating team member:', error);
      const errorMessage =
        error.response?.data?.error ||
        'An error occurred while creating the team member.';
      toast.error(errorMessage);
    }
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    email: Yup.string()
      .email('E-mail must be a valid address')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .when([], {          // no deps → run once
        is: () => type !== 'edit',
        then: (schema) => schema.required('Password is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    phone: Yup.string()
      .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone must be (XXX) XXX-XXXX')
      .required('Phone is required'),
    clinic_role_id: Yup.string().required('Role is required'),
  });

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, ''); // Remove non-numeric characters
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return value;

    const formatted = !match[2]
      ? match[1]
      : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;

    return formatted.length > 14 ? formatted.slice(0, 14) : formatted; // Limit to 14 characters
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: memberData?.first_name || '',
      last_name: memberData?.last_name || '',
      email: memberData?.email || '',
      designation: memberData?.designation || '',
      password: '',
      phone: memberData?.phone || '',
      address: memberData?.address || '',
      clinic_role_id: memberData?.clinic_role_id || '',
    },
    validationSchema,
    onSubmit: handleCreate,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setMemberData([]);
        setSelectedDashboards([]);
        setDashboardsError(false);
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent borderRadius="16px" pb="16px">
        <ModalHeader color="brand.500">
          {type === 'edit' ? 'Edit Team Member' : 'Add Team Member'}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={formik.handleSubmit}>
          <ModalBody maxHeight="475px" overflowY="auto" pb={2}>
            {[
              'first_name',
              'last_name',
              'designation',
              'phone',
              'address',
            ].map((field) => (
              <FormControl
                key={field}
                isInvalid={formik.touched[field] && formik.errors[field]}
                mb={2}
              >
                <FormLabel mb={0} textTransform="capitalize">
                  {field.replace('_', ' ')}
                </FormLabel>

                <Input
                  name={field}
                  type={field === 'phone' ? 'tel' : 'text'}
                  autoComplete={field === 'phone' ? 'tel' : 'off'}
                  placeholder={`Enter ${field.replace('_', ' ')}`}
                  variant="auth"
                  fontSize="sm"
                  fontWeight="500"
                  maxLength={field === 'phone' ? 14 : undefined}
                  value={formik.values[field]}
                  onChange={(e) =>
                    field === 'phone'
                      ? formik.handleChange({
                        target: {
                          name: 'phone',
                          value: formatPhoneNumber(e.target.value),
                        },
                      })
                      : formik.handleChange(e)
                  }
                  onBlur={formik.handleBlur}
                />

                {/* Helper note for phone field */}
                {field === 'phone' && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    For dummy numbers, use <b>(000) 000-0000</b> or enter a valid 10-digit phone number in the format (XXX) XXX-XXXX.
                  </Text>
                )}

                {formik.touched[field] && formik.errors[field] && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors[field]}
                  </Text>
                )}
              </FormControl>
            ))}

            {/*–– EMAIL ––*/}
            <FormControl
              isInvalid={formik.touched.email && formik.errors.email}
              mb={2}
            >
              <FormLabel mb={0}>Email</FormLabel>
              <Input
                name="email"
                type="email"
                autoComplete="username"
                placeholder="Enter email"
                variant="auth"
                fontSize="sm"
                fontWeight="500"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isDisabled={type === 'edit'}
              />
              {formik.touched.email && formik.errors.email && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.email}
                </Text>
              )}
            </FormControl>

            {/*–– ROLE ––*/}
            <FormControl
              isInvalid={formik.touched.clinic_role_id && formik.errors.clinic_role_id}
              mb={2}
            >
              <FormLabel mb={0}>Role</FormLabel>
              <Select
                name="clinic_role_id"
                variant="auth"
                fontSize="sm"
                fontWeight="500"
                placeholder="Select role"
                value={formik.values.clinic_role_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isDisabled={loading}
                textTransform="capitalize"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.role_name}
                  </option>
                ))}
              </Select>
              {formik.touched.clinic_role_id && formik.errors.clinic_role_id && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.clinic_role_id}
                </Text>
              )}
            </FormControl>

            {/*–– PASSWORD ––*/}
            <FormControl
              isInvalid={formik.touched.password && formik.errors.password}
              mb={2}
            >
              <FormLabel mb={0}>Password</FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={type === 'edit' ? 'current-password' : 'new-password'}
                  placeholder="Enter password"
                  variant="auth"
                  fontSize="sm"
                  fontWeight="500"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <InputRightElement>
                  <Icon
                    as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handlePasswordVisibility}
                    cursor="pointer"
                  />
                </InputRightElement>
              </InputGroup>
              {formik.touched.password && formik.errors.password && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.password}
                </Text>
              )}
            </FormControl>

            {/*–– DASHBOARDS CHECKBOXES ––*/}
            <FormControl isInvalid={dashboardsError} mb={dashboardsError ? 0 : 2}>
              <FormLabel>Select Dashboards</FormLabel>
              <VStack align="start">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                  {dashboards.map((location) => (
                    <Checkbox
                      key={location.id}
                      isChecked={selectedDashboards.includes(location.id)}
                      onChange={() => handleDashboardChange(location.id)}
                    >
                      <span style={{ fontSize: '.85rem', textTransform: "capitalize" }}>
                        {location.name}
                      </span>
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </VStack>
              {dashboardsError && (
                <Text mt={1} color="red.500" fontSize="sm">
                  Select at least one dashboard
                </Text>
              )}
            </FormControl>
          </ModalBody>

          <ModalFooter pt={0}>
            <Button type="submit" colorScheme="brand" w="100%" mt={4} isLoading={loading}>
              {type === 'edit' ? 'Update Member' : 'Add Member'}
            </Button>
          </ModalFooter>
        </form>

      </ModalContent>
    </Modal>
  );
};

export default TeamMemberModal;



// import React, { useEffect, useState } from 'react';
// import {
//   Button,
//   FormControl,
//   FormLabel,
//   Input,
//   Text,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalFooter,
//   ModalBody,
//   ModalCloseButton,
//   InputGroup,
//   InputRightElement,
//   Icon,
//   Select,
//   VStack,
//   SimpleGrid,
//   Checkbox,
// } from '@chakra-ui/react';
// import { useFormik } from 'formik';
// import * as Yup from 'yup';
// import { MdOutlineRemoveRedEye } from 'react-icons/md';
// import { RiEyeCloseLine } from 'react-icons/ri';
// import { createAPIEndPoint } from 'config/api/api';
// import toast from 'react-hot-toast';
// import { getUserData } from 'utils';

// const TeamMemberModal = ({ isOpen, onClose, type, memeberId, fetchAgain }) => {
//   const userData = getUserData();
//   const clinic_id = userData?.clinic_id ?? null;
//   const dashboard_id = userData?.dashboard?.id ?? null;
//   const role_id = userData?.role?.id ?? null;
//   const [showPassword, setShowPassword] = useState(false);
//   const [roles, setRoles] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const handlePasswordVisibility = () => setShowPassword(!showPassword);

//   const [memberData, setMemberData] = useState([]);

//   const [dashboards, setDashboards] = useState([]);
//   console.log(" TeamMemberModal ~ dashboards:", dashboards)
//   const [selectedDashboards, setSelectedDashboards] = useState([]);
//   const [dashboardsError, setDashboardsError] = useState(false)
//   console.log(" TeamMemberModal ~ selectedDashboards:", selectedDashboards)


//   const fetchDashboards = async () => {
//     try {
//       const response = await createAPIEndPoint('dashboard').fetchAll();
//       console.log(" fetchDashboards ~ response:", response)
//       const dashboards = response.data.dashboards || [];

//       // Map locations to format name, city, and state
//       const formattedLocations = dashboards.map((dashboard) => ({
//         id: dashboard.id,
//         name: `${dashboard.name}`, // Display name with city & state
//       }));

//       setDashboards(formattedLocations);
//     } catch (err) {
//       toast.error(err?.response?.data?.error || 'Failed to fetch locations');
//     }
//   };


//   const handleDashboardChange = (id) => {
//     setSelectedDashboards((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
//     );
//     if (dashboardsError) setDashboardsError(false);                   // clear inline error
//   };

//   // replace your existing fetchMember with this:
//   const fetchMember = async () => {
//     try {
//       const response = await createAPIEndPoint('clinic_team/').fetchById(memeberId);
//       const m = response.data.team_member || {};
//       setMemberData(m);
//       // use dashboard_ids (array) instead of dashboard_id
//       setSelectedDashboards(m.dashboard_ids || []);
//     } catch (err) {
//       toast.error(err?.response?.data?.error || 'Error fetching team member');
//     }
//   };


//   // useEffect(() => {
//   //   if (memeberId) {
//   //     fetchMember();
//   //   }
//   // }, [memeberId]);

//   useEffect(() => {
//     if (isOpen) {
//       fetchDashboards()
//     }
//   }, [isOpen]);

//   useEffect(() => {
//     if (memeberId && type === 'edit') {
//       fetchMember();
//     }
//   }, [memeberId, type, isOpen]);

//   useEffect(() => {
//     const getRoles = async () => {
//       try {
//         const response = await createAPIEndPoint(`clinic_roles/get_all/${clinic_id}`).fetchAll();
//         setRoles(response.data.roles);
//       } catch (error) {
//         toast.error(error?.response?.data?.error || 'Error fetching roles');
//       }
//     };
//     if (isOpen) {
//       getRoles();
//     }
//   }, [isOpen]);

//   const handleCreate = async (values, { resetForm }) => {
//     if (selectedDashboards.length === 0) {
//       setDashboardsError(true);
//       toast.error('Select at least one dashboard');
//       return;          // stop right here
//     }
//     try {
//       setLoading(true);

//       if (memeberId) {
//         await createAPIEndPoint('clinic_team/').update(memeberId, {
//           ...values,
//           dashboard_id: selectedDashboards,
//           clinic_id,
//           role_id,
//           clinic_role_id: parseInt(values.clinic_role_id, 10),
//           invited_by_id: 2,

//         });
//       } else {
//         await createAPIEndPoint('clinic_team').create({
//           ...values,
//           dashboard_id: selectedDashboards,
//           clinic_id,
//           status: 'enable',
//           role_id,
//           clinic_role_id: parseInt(values.clinic_role_id, 10),
//           invited_by_id: 2,
//         });
//       }

//       toast.success('New team member added successfully!');
//       setLoading(false);
//       fetchAgain();
//       resetForm();
//       onClose();
//     } catch (error) {
//       setLoading(false);
//       console.error('Error creating team member:', error);
//       const errorMessage =
//         error.response?.data?.error ||
//         'An error occurred while creating the team member.';
//       toast.error(errorMessage);
//     }
//   };

//   const validationSchema = Yup.object().shape({
//     first_name: Yup.string().required('First name is required'),
//     last_name: Yup.string().required('Last name is required'),
//     email: Yup.string()
//       .matches(
//         /^[a-zA-Z0-9._%+-]+@[A-Za-z0-9.-]+\.com$/i,  // must end with .com
//         'E-mail must be a valid .com address',
//       )
//       .required('Email is required'),
//     password: Yup.string()
//       .min(8, 'Password must be at least 8 characters')
//       .when([], {          // no deps → run once
//         is: () => type !== 'edit',
//         then: (schema) => schema.required('Password is required'),
//         otherwise: (schema) => schema.notRequired(),
//       }),
//     phone: Yup.string()
//       .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone must be (XXX) XXX-XXXX')
//       .required('Phone is required'),
//     clinic_role_id: Yup.string().required('Role is required'),
//   });

//   const formatPhoneNumber = (value) => {
//     const cleaned = value.replace(/\D/g, ''); // Remove non-numeric characters
//     const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

//     if (!match) return value;

//     const formatted = !match[2]
//       ? match[1]
//       : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;

//     return formatted.length > 14 ? formatted.slice(0, 14) : formatted; // Limit to 14 characters
//   };

//   const formik = useFormik({
//     enableReinitialize: true,
//     initialValues: {
//       first_name: memberData?.first_name || '',
//       last_name: memberData?.last_name || '',
//       email: memberData?.email || '',
//       designation: memberData?.designation || '',
//       password: '',
//       phone: memberData?.phone || '',
//       address: memberData?.address || '',
//       clinic_role_id: memberData?.clinic_role_id || '',
//     },
//     validationSchema,
//     onSubmit: handleCreate,
//   });

//   return (
//     <Modal
//       isOpen={isOpen}
//       onClose={() => {
//         onClose();
//         setMemberData([]);
//         setSelectedDashboards([]);
//         setDashboardsError(false);
//       }}
//       isCentered
//     >
//       <ModalOverlay />
//       <ModalContent borderRadius="16px" pb="16px">
//         <ModalHeader color="brand.500">
//           {type === 'edit' ? 'Edit Team Member' : 'Add Team Member'}
//         </ModalHeader>
//         <ModalCloseButton />
//         <form onSubmit={formik.handleSubmit}>
//           <ModalBody maxHeight="475px" overflowY="auto" pb={2}>
//             {[
//               'first_name',
//               'last_name',
//               'designation',
//               'phone',
//               'address',
//             ].map((field) => (
//               <FormControl
//                 key={field}
//                 isInvalid={formik.touched[field] && formik.errors[field]}
//                 mb={2}
//               >
//                 <FormLabel mb={0} textTransform="capitalize">
//                   {field.replace('_', ' ')}
//                 </FormLabel>
//                 <Input
//                   name={field}
//                   type={field === 'phone' ? 'tel' : 'text'}
//                   autoComplete={
//                     field === 'phone'
//                       ? 'tel'
//                       : 'off'
//                   }
//                   placeholder={`Enter ${field.replace('_', ' ')}`}
//                   variant="auth"
//                   fontSize="sm"
//                   fontWeight="500"
//                   maxLength={field === 'phone' ? 14 : undefined}
//                   value={formik.values[field]}
//                   onChange={(e) =>
//                     field === 'phone'
//                       ? formik.handleChange({
//                         target: {
//                           name: 'phone',
//                           value: formatPhoneNumber(e.target.value),
//                         },
//                       })
//                       : formik.handleChange(e)
//                   }
//                   onBlur={formik.handleBlur}
//                 />
//                 {formik.touched[field] && formik.errors[field] && (
//                   <Text color="red.500" fontSize="sm">
//                     {formik.errors[field]}
//                   </Text>
//                 )}
//               </FormControl>
//             ))}

//             {/*–– EMAIL ––*/}
//             <FormControl
//               isInvalid={formik.touched.email && formik.errors.email}
//               mb={2}
//             >
//               <FormLabel mb={0}>Email</FormLabel>
//               <Input
//                 name="email"
//                 type="email"
//                 autoComplete="username"
//                 placeholder="Enter email"
//                 variant="auth"
//                 fontSize="sm"
//                 fontWeight="500"
//                 value={formik.values.email}
//                 onChange={formik.handleChange}
//                 onBlur={formik.handleBlur}
//                 isDisabled={type === 'edit'}
//               />
//               {formik.touched.email && formik.errors.email && (
//                 <Text color="red.500" fontSize="sm">
//                   {formik.errors.email}
//                 </Text>
//               )}
//             </FormControl>

//             <FormControl
//               isInvalid={
//                 formik.touched.clinic_role_id && formik.errors.clinic_role_id
//               }
//               mb={2}
//             >
//               <FormLabel mb={0}>Role</FormLabel>
//               <Select
//                 name="clinic_role_id"
//                 variant="auth"
//                 fontSize="sm"
//                 fontWeight="500"
//                 placeholder="Select role"
//                 value={formik.values.clinic_role_id}
//                 onChange={formik.handleChange}
//                 onBlur={formik.handleBlur}
//                 isDisabled={loading}
//                 textTransform="capitalize"
//               >
//                 {roles.map((role) => (
//                   <option key={role.id} value={role.id}>
//                     {role.role_name}
//                   </option>
//                 ))}
//               </Select>
//               {formik.touched.clinic_role_id &&
//                 formik.errors.clinic_role_id && (
//                   <Text color="red.500" fontSize="sm">
//                     {formik.errors.clinic_role_id}
//                   </Text>
//                 )}
//             </FormControl>

//             {/*–– PASSWORD ––*/}
//             <FormControl
//               isInvalid={formik.touched.password && formik.errors.password}
//               mb={2}
//             >
//               <FormLabel mb={0}>Password</FormLabel>
//               <InputGroup>
//                 <Input
//                   name="password"
//                   type={showPassword ? 'text' : 'password'}
//                   autoComplete={type === 'edit' ? 'current-password' : 'new-password'}
//                   placeholder="Enter password"
//                   variant="auth"
//                   fontSize="sm"
//                   fontWeight="500"
//                   value={formik.values.password}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                 />
//                 <InputRightElement>
//                   <Icon
//                     as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
//                     onClick={handlePasswordVisibility}
//                     cursor="pointer"
//                   />
//                 </InputRightElement>
//               </InputGroup>
//               {formik.touched.password && formik.errors.password && (
//                 <Text color="red.500" fontSize="sm">
//                   {formik.errors.password}
//                 </Text>
//               )}
//             </FormControl>



//             {/*–– DASHBOARDS CHECKBOXES ––*/}
//             <FormControl
//               isInvalid={dashboardsError}
//               mb={dashboardsError ? 0 : 2}
//             >
//               <FormLabel>Select Dashboards</FormLabel>
//               <VStack align="start">
//                 <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
//                   {dashboards.map((location) => (
//                     <Checkbox
//                       key={location.id}
//                       isChecked={selectedDashboards.includes(location.id)}
//                       onChange={() => handleDashboardChange(location.id)}
//                     >
//                       <span style={{ fontSize: '.85rem', textTransform: "capitalize" }}>
//                         {location.name}
//                       </span>
//                     </Checkbox>
//                   ))}
//                 </SimpleGrid>
//               </VStack>
//               {dashboardsError && (
//                 <Text mt={1} color="red.500" fontSize="sm">
//                   Select at least one dashboard
//                 </Text>
//               )}
//             </FormControl>
//           </ModalBody>

//           <ModalFooter pt={0}>
//             <Button type="submit" colorScheme="brand" w="100%" mt={4} isLoading={loading}>
//               {type === 'edit' ? 'Update Member' : 'Add Member'}
//             </Button>
//           </ModalFooter>
//         </form>
//       </ModalContent>
//     </Modal>
//   );
// };

// export default TeamMemberModal;
